import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  MAX_VOICE_MEMO_DURATION_MS,
  VOICE_MEMO_AUDIO_BITS_PER_SECOND,
} from '@common/constants/atlasai'
import moment from 'moment'
import { formatClockDuration } from '../helpers/formatClockDuration'
import { useDispatch } from '../redux/hooks'
import { logError } from '../redux/logger/actions'

/**
 * Microphone capture for a voice memo: getUserMedia, the elapsed timer, the
 * auto-stop at {@link MAX_VOICE_MEMO_DURATION_MS}, and handing the finished
 * recording back as a ready-to-upload File.
 *
 * Modelled on {@link useScreenRecording} (the FeedbackWidget's screen capture),
 * with two deliberate differences: microphone failures are reported instead of
 * swallowed — a blocked permission has to be explainable to the user — and the
 * elapsed time is measured against a wall clock rather than counted in ticks, so
 * a throttled background tab still stops at the cap.
 *
 * Delivery is a callback rather than a returned blob because a recording can end
 * three ways — the user stops it, it hits the cap, or the mic track ends — and the
 * caller wants one path for all three.
 */

/** Why capture failed. Copy lives with the caller, which knows its own surface. */
export enum VoiceRecordingError {
  /** The user denied the prompt, or the browser/OS has the mic blocked. */
  PermissionDenied = 'permission-denied',
  /** No input device at all. */
  NoMicrophone = 'no-microphone',
  /** No MediaRecorder, or no audio container it can produce. */
  Unsupported = 'unsupported',
  /** Anything else — device busy, hardware error. */
  Failed = 'failed',
}

export interface IVoiceRecording {
  /** Named with an extension matching the container, which the server validates. */
  file: File
  durationMs: number
  /** Loudness samples, 0–1, one per {@link LEVEL_SAMPLE_MS}. Empty without Web Audio. */
  levels: number[]
}

/** How often loudness is sampled for the waveform. */
export const LEVEL_SAMPLE_MS = 100

// Speech RMS rarely passes ~0.2 of full scale, so it is stretched to fill the bars.
const LEVEL_GAIN = 5

type AudioContextConstructor = typeof AudioContext

export const getAudioContextConstructor = (): AudioContextConstructor | null =>
  typeof window === 'undefined'
    ? null
    : (window.AudioContext ??
      (window as unknown as { webkitAudioContext?: AudioContextConstructor }).webkitAudioContext ??
      null)

const readLevel = (analyser: AnalyserNode, buffer: Uint8Array<ArrayBuffer>): number => {
  analyser.getByteTimeDomainData(buffer)
  let sumOfSquares = 0
  for (const sample of buffer) {
    const centred = (sample - 128) / 128
    sumOfSquares += centred * centred
  }
  return Math.min(1, Math.sqrt(sumOfSquares / buffer.length) * LEVEL_GAIN)
}

// Ordered by preference. Chromium and Firefox take webm/opus; Safari only ever
// produces mp4. Every extension here is in the server's AUDIO_FILE_TYPES.
const CANDIDATE_CONTAINERS: Array<{ mimeType: string; extension: string }> = [
  { mimeType: 'audio/webm', extension: 'webm' },
  { mimeType: 'audio/mp4', extension: 'mp4' },
  { mimeType: 'audio/ogg', extension: 'ogg' },
]

export const pickContainer = (): { mimeType: string; extension: string } | null => {
  if (typeof MediaRecorder === 'undefined') return null
  return CANDIDATE_CONTAINERS.find((c) => MediaRecorder.isTypeSupported(c.mimeType)) ?? null
}

/**
 * `navigator.mediaDevices` only exists in a secure context, so this is false when
 * the app is served over plain http from anything but localhost — which is how a
 * dev testing on a LAN address hits it. Reported as unsupported rather than as a
 * generic failure, so the message names the real problem.
 */
export const canCaptureAudio = (): boolean =>
  typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia

export const classifyCaptureError = (err: unknown): VoiceRecordingError => {
  const name = (err as { name?: string })?.name
  if (name === 'NotAllowedError' || name === 'SecurityError') {
    return VoiceRecordingError.PermissionDenied
  }
  if (name === 'NotFoundError' || name === 'DevicesNotFoundError') {
    return VoiceRecordingError.NoMicrophone
  }
  // What MediaRecorder throws when it can't produce the container isTypeSupported
  // approved — a real codec gap, not a failure worth retrying.
  if (name === 'NotSupportedError') {
    return VoiceRecordingError.Unsupported
  }
  return VoiceRecordingError.Failed
}

/**
 * Flattens a thrown capture error for the alert. The name is the useful part —
 * `NotSupportedError` is a codec problem, `NotReadableError` a device one.
 */
const describeCaptureError = (err: unknown): { name: string; message: string; stack: string } => {
  if (typeof err === 'string') {
    return { name: 'UnknownError', message: err, stack: '' }
  }
  const { name, message, stack } = (err ?? {}) as {
    name?: string
    message?: string
    stack?: string
  }
  return { name: name ?? 'UnknownError', message: message ?? 'no message', stack: stack ?? '' }
}

interface IParams {
  /** Called once per completed recording, however it ended. Never on unmount. */
  onComplete: (recording: IVoiceRecording) => void
}

const useVoiceRecording = ({ onComplete }: IParams) => {
  const dispatch = useDispatch()
  const [isRecording, setIsRecording] = useState(false)
  const [recordingSeconds, setRecordingSeconds] = useState(0)
  const [error, setError] = useState<VoiceRecordingError | null>(null)

  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const startedAtRef = useRef<number | null>(null)
  // Set when the recording is being torn down rather than delivered (unmount or
  // cancel), so `onstop` knows to stay quiet.
  const isDiscardingRef = useRef(false)
  // Guards the await inside startRecording. The permission prompt can sit open for
  // seconds with the mic button still live, and two clicks would both pass an
  // `isRecording` state check — the second recorder then orphans the first stream
  // and leaves the microphone on.
  const isStartingRef = useRef(false)
  // Set when a start is cancelled while its permission prompt is still open, so the
  // grant that follows drops the mic instead of recording.
  const isStartAbandonedRef = useRef(false)
  // Whether the hook is still mounted. Checked after the getUserMedia await: the
  // permission prompt can outlive the composer, and the unmount cleanup below runs
  // while `streamRef` is still empty, so it has nothing to stop. Without this the
  // continuation would attach a live microphone to a dead hook.
  const isMountedRef = useRef(true)
  // Waveform sampling. Best-effort: without Web Audio a memo records with no levels.
  const audioContextRef = useRef<AudioContext | null>(null)
  const levelTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const levelsRef = useRef<number[]>([])

  // Held in a ref so a caller passing a fresh closure each render doesn't have to
  // memoize it to keep startRecording stable.
  const onCompleteRef = useRef(onComplete)
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current)
    timerRef.current = null
  }, [])

  const closeAudio = useCallback(() => {
    if (levelTimerRef.current) clearInterval(levelTimerRef.current)
    levelTimerRef.current = null
    void audioContextRef.current?.close().catch(() => {})
    audioContextRef.current = null
  }, [])

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    closeAudio()
  }, [closeAudio])

  const startLevelSampling = useCallback((stream: MediaStream) => {
    const context = audioContextRef.current
    levelsRef.current = []
    if (!context) return
    try {
      const analyser = context.createAnalyser()
      analyser.fftSize = 512
      context.createMediaStreamSource(stream).connect(analyser)
      const buffer = new Uint8Array(analyser.fftSize)
      levelTimerRef.current = setInterval(() => {
        levelsRef.current.push(readLevel(analyser, buffer))
      }, LEVEL_SAMPLE_MS)
    } catch {
      // The waveform is decoration; the recording goes ahead without it.
    }
  }, [])

  /** The levels captured so far, for a live waveform to draw without re-rendering us. */
  const getLevels = useCallback((): readonly number[] => levelsRef.current, [])

  // Drop the mic on unmount without delivering: the composer that asked for the
  // recording is gone. Also stops the browser's recording indicator.
  useEffect(() => {
    return () => {
      isMountedRef.current = false
      isDiscardingRef.current = true
      if (timerRef.current) clearInterval(timerRef.current)
      streamRef.current?.getTracks().forEach((track) => track.stop())
      if (levelTimerRef.current) clearInterval(levelTimerRef.current)
      void audioContextRef.current?.close().catch(() => {})
    }
  }, [])

  /**
   * Send a capture failure to the app's client-error alert. The user only ever sees
   * one of four generic messages, so the thrown error — the only thing naming the
   * codec or device that refused — would otherwise live and die in a catch block.
   * A denied permission or an absent device is skipped: expected, already explained
   * to the user, and alerting on them would bury the codec/OS failures this is for.
   */
  const reportCaptureFailure = useCallback(
    (err: unknown, context: string) => {
      const kind = classifyCaptureError(err)
      if (
        kind === VoiceRecordingError.PermissionDenied ||
        kind === VoiceRecordingError.NoMicrophone
      ) {
        return
      }

      const { name, message, stack } = describeCaptureError(err)
      void dispatch(
        logError({
          message: `Voice memo capture failed (${context}): ${name} — ${message}`,
          url: window.location.href,
          stack,
        }),
      )
    },
    [dispatch],
  )

  /**
   * Tear down after the microphone was acquired but recording could not start. The
   * stream is already live at that point, so leaving it would keep the browser's
   * recording indicator on with nothing recording and no control able to stop it.
   */
  const failCapture = useCallback(
    (err: unknown, context: string) => {
      clearTimer()
      releaseStream()
      recorderRef.current = null
      startedAtRef.current = null
      setIsRecording(false)
      setError(classifyCaptureError(err))
      reportCaptureFailure(err, context)
    },
    [clearTimer, releaseStream, reportCaptureFailure],
  )

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      // `onstop` finishes the teardown once the last chunk has flushed.
      recorder.stop()
      return
    }
    clearTimer()
    releaseStream()
    setIsRecording(false)
  }, [clearTimer, releaseStream])

  /** Throw the recording away — `onComplete` never fires for it — or abandon one still starting. */
  const cancelRecording = useCallback(() => {
    if (isStartingRef.current) isStartAbandonedRef.current = true
    isDiscardingRef.current = true
    stopRecording()
  }, [stopRecording])

  const startRecording = useCallback(async () => {
    if (isStartingRef.current || recorderRef.current?.state === 'recording') return
    isStartingRef.current = true
    isStartAbandonedRef.current = false
    try {
      const container = pickContainer()
      if (!container || !canCaptureAudio()) {
        setError(VoiceRecordingError.Unsupported)
        return
      }

      setError(null)

      // Created before the permission prompt, while the click still counts as a user
      // gesture: Safari starts a context made after the await suspended, and silent.
      const AudioContextImpl = getAudioContextConstructor()
      if (AudioContextImpl && !audioContextRef.current) {
        try {
          audioContextRef.current = new AudioContextImpl()
        } catch {
          audioContextRef.current = null
        }
      }

      let stream: MediaStream
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      } catch (err) {
        closeAudio()
        setError(classifyCaptureError(err))
        // Reports too: a busy or failing device (NotReadableError) is as opaque.
        reportCaptureFailure(err, 'getUserMedia')
        return
      }

      // The composer went away while the permission prompt was open. Drop the mic
      // now: going on to build a recorder would leave it live, with the timer
      // running and no UI able to stop it, until the cap sent a memo from a
      // composer that no longer exists.
      if (!isMountedRef.current || isStartAbandonedRef.current) {
        stream.getTracks().forEach((track) => track.stop())
        closeAudio()
        return
      }

      streamRef.current = stream
      isDiscardingRef.current = false

      const chunks: BlobPart[] = []
      // Can throw even though isTypeSupported approved the container: the bitrate
      // hint may be rejected for that codec, or capture can fail at the OS level.
      let recorder: MediaRecorder
      try {
        recorder = new MediaRecorder(stream, {
          mimeType: container.mimeType,
          audioBitsPerSecond: VOICE_MEMO_AUDIO_BITS_PER_SECOND,
        })
      } catch (err) {
        failCapture(err, `new MediaRecorder, ${container.mimeType}`)
        return
      }
      recorderRef.current = recorder

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunks.push(event.data)
      }

      recorder.onstop = () => {
        clearTimer()
        releaseStream()
        const levels = levelsRef.current
        levelsRef.current = []
        setIsRecording(false)

        // Clamped to the cap on purpose. The auto-stop below runs off a one-second
        // tick, so the measured time always overshoots a little — and a backgrounded
        // tab, where browsers throttle timers to roughly once a minute, can overshoot
        // by a lot. Reporting the raw value would have the server's duration backstop
        // reject the very recording the auto-stop just rescued. The byte-size guard is
        // what actually bounds an over-long memo (five minutes of Opus is a few MB
        // against a 25 MB ceiling).
        const elapsedMs = startedAtRef.current ? Date.now() - startedAtRef.current : 0
        const durationMs = Math.min(elapsedMs, MAX_VOICE_MEMO_DURATION_MS)
        startedAtRef.current = null

        if (isDiscardingRef.current) {
          isDiscardingRef.current = false
          return
        }

        const blob = new Blob(chunks, { type: container.mimeType })
        if (blob.size === 0) {
          setError(VoiceRecordingError.Failed)
          return
        }

        const fileName = `voice-memo-${moment().format('YYYY-MM-DD_HH-mm-ss')}.${container.extension}`
        onCompleteRef.current({
          file: new File([blob], fileName, { type: container.mimeType }),
          durationMs,
          levels,
        })
      }

      // The mic going away mid-recording (unplugged, revoked in browser settings)
      // should deliver what was captured rather than hang on a dead recorder.
      const [audioTrack] = stream.getAudioTracks()
      if (audioTrack) {
        audioTrack.onended = () => {
          if (recorder.state !== 'inactive') recorder.stop()
        }
      }

      try {
        recorder.start()
      } catch (err) {
        failCapture(err, `recorder.start, ${container.mimeType}`)
        return
      }

      startedAtRef.current = Date.now()
      startLevelSampling(stream)
      setRecordingSeconds(0)
      setIsRecording(true)

      timerRef.current = setInterval(() => {
        const elapsedMs = Date.now() - (startedAtRef.current ?? Date.now())
        setRecordingSeconds(Math.floor(elapsedMs / 1000))
        // Stopping at the cap is what keeps an over-length memo from ever being
        // recorded; the server's duration check is only a backstop.
        if (elapsedMs >= MAX_VOICE_MEMO_DURATION_MS && recorder.state !== 'inactive') {
          recorder.stop()
        }
      }, 1000)
    } finally {
      isStartingRef.current = false
    }
  }, [clearTimer, releaseStream, closeAudio, failCapture, reportCaptureFailure, startLevelSampling])

  const clearError = useCallback(() => setError(null), [])

  const formattedRecordingTime = useMemo(
    () => formatClockDuration(recordingSeconds),
    [recordingSeconds],
  )

  return {
    isRecording,
    recordingSeconds,
    formattedRecordingTime,
    error,
    clearError,
    startRecording,
    stopRecording,
    cancelRecording,
    getLevels,
  }
}

export default useVoiceRecording
